import React, { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { cssFontFamily } from '../../config/fonts';
import { TextElement } from '../../model/types';

interface TextEditorOverlayProps {
  element: TextElement;
  zoom: number;
  onCommit: (text: string) => void;
  onCancel: () => void;
}

/**
 * Edycja tekstu odbywa się w polu HTML ustawionym dokładnie nad węzłem Konvy.
 * Węzeł na ten czas jest ukryty, więc użytkownik widzi jeden tekst, ale pisze
 * w natywnym polu (kursor, zaznaczanie, wklejanie działają jak zwykle).
 */
export const TextEditorOverlay: FC<TextEditorOverlayProps> = ({ element, zoom, onCommit, onCancel }) => {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [value, setValue] = useState(element.text);

  useEffect(() => {
    const node = inputRef.current;
    if (!node) return;
    node.focus();
    node.setSelectionRange(node.value.length, node.value.length);
  }, []);

  useLayoutEffect(() => {
    const node = inputRef.current;
    if (!node) return;
    node.style.height = 'auto';
    node.style.height = `${node.scrollHeight}px`;
  }, [value, zoom]);

  const commit = (): void => onCommit(value);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onCancel();
    }
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      commit();
    }
    event.stopPropagation();
  };

  return (
    <div
      style={{
        position: 'absolute',
        left: element.x * zoom,
        top: element.y * zoom,
        transform: `rotate(${element.rotation}deg)`,
        transformOrigin: '0 0',
        padding: element.padding * zoom,
        width: element.width * zoom,
      }}
    >
      <textarea
        ref={inputRef}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        style={{
          width: '100%',
          margin: 0,
          padding: 0,
          border: 'none',
          outline: 'none',
          resize: 'none',
          overflow: 'hidden',
          background: 'transparent',
          color: element.color,
          fontFamily: cssFontFamily(element.fontFamily),
          fontSize: element.fontSize * zoom,
          fontWeight: element.fontWeight,
          fontStyle: element.fontStyle,
          lineHeight: element.lineHeight,
          letterSpacing: element.letterSpacing * zoom,
          textAlign: element.align,
          textTransform: element.uppercase ? 'uppercase' : 'none',
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word',
        }}
      />
    </div>
  );
};
